import { z } from 'zod';

// Helper function to capitalize the first letter and make the rest lowercase
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const createUserSchema = (t: (key: string, defaultText: string) => string) => {
  return z.object({
  FirstName: z.string()
    .min(1, { message: t("validation_firstName", "First Name is required.") })
    // Regex to ensure only a-z characters (case-insensitive) are present in the input
    .regex(/^[a-zA-Z0-9]+$/, { message: t("validation_firstName_specialChar", "First Name cannot contain special characters.") })
    // Transform the input to have the first letter uppercase and rest lowercase
    .transform((name) => capitalizeFirstLetter(name)),

  LastName: z.string()
    .min(1, { message: t("validation_lastName", "Last Name is required.") })
    .regex(/^[a-zA-Z0-9]+$/, { message: t("validation_lastName_specialChar", "Last Name cannot contain special characters.") })
    .transform((name) => capitalizeFirstLetter(name)),

  MiddleName: z.string()
    .optional() // Middle name is optional
    // Only apply transform if middle name is provided and not empty
    .transform((name) => (name ? capitalizeFirstLetter(name) : name))
    .refine((name) => {
      // If middle name is provided, ensure it only contains a-z characters
      if (name && !/^[a-zA-Z]+$/.test(name)) {
        throw new z.ZodError([{ code: z.ZodIssueCode.custom, message: t("validation_midName", "Middle Name should only contain letters."), path: ['MiddleName'] }]);
      }
      return true;
    }, {
      // Fallback message for consistency, though the throw above is more specific
      message: "Middle Name should only contain letters."
    }),

  Email: z.preprocess(
    (val) => (val === '' ? undefined : val), z.string().email({ message: t("validation_emailFormat", "Invalid email address format.") }).optional()),

  PhoneNumber: z.preprocess(
    (val) => (val === '' ? undefined : val), // If value is an empty string, convert it to undefined
    z.string() // The rest of the schema applies IF val is a string (i.e., not undefined)
      .regex(/^[\d\-\(\)\s]+$/, t("validation_phoneChar", "Phone number can only contain digits, hyphens, parentheses, and spaces."))
      .transform((val) => val.replace(/[\D]/g, ''))
      .refine((val) => val.length === 10, t("validation_phoneLength","Phone number must be exactly 10 digits.")).optional()
  ),

  DateOfBirth: z.string()
    .min(1, { message: t("validation_dob", "Date of Birth is required.") })
    // Validate the YYYY-MM-DD format (from type="date" input)
    .regex(/^\d{4}-\d{2}-\d{2}$/, t("validation_dobFormat", "Date of Birth must be in MM/DD/YYYY format (e.g., 09/16/2006)."))
    // Check for actual date validity (e.g., catches "2023-02-30")
    .refine((dobString) => {
      const [year, month, day] = dobString.split('-').map(Number);
      const dateObject = new Date(year, month - 1, day); 

      return (
        dateObject.getFullYear() === year &&
        dateObject.getMonth() === month - 1 && 
        dateObject.getDate() === day
      );
    }, {
      message: "Invalid Date of Birth. Please enter a valid date (e.g., 2006-09-16).",
    })
    .transform(dobYYYYMMDD => {
      const [year, month, day] = dobYYYYMMDD.split('-');
      return `${month}/${day}/${year}`;
    }),
})};

export type ValidationUser = z.infer<ReturnType<typeof createUserSchema>>;


export const userInternalSchema = z.object({
   ClientId: z.string().optional(),
  SurveyType: z.string().optional(),
});

export type ValidationUserInternal = z.infer<typeof userInternalSchema>;